"use client";

import { BarList } from "@/components/analytics/bar-list";
import {
  ACCENT,
  DistributionChart,
  NEUTRAL,
  PageFlowChart,
  ShareRing,
  SOFT,
  StatTile,
  ViewsAreaChart,
} from "@/components/analytics-workspace/charts";
import { getModuleTypeMeta } from "@/lib/module-type-meta";
import type { DraggableModuleType } from "@/types/modules";
import {
  BarChart3,
  Clock,
  Eye,
  Globe,
  MousePointerClick,
  Smartphone,
  Star,
  ThumbsUp,
  Timer,
  Workflow,
} from "lucide-react";
import { MockFrame } from "./mock-label";

const percent = (value: number) => `${value} %`;

const VIEWS_PER_PAGE = [128, 121, 117, 112, 96, 84, 79, 74].map(
  (value, index) => ({ label: `${index + 1}`, value }),
);

const REACH_PER_PAGE = [100, 95, 91, 88, 63, 59, 56, 54].map(
  (value, index) => ({ label: `${index + 1}`, value }),
);

const SESSION_LENGTH = [
  { label: "< 10 s", value: 14 },
  { label: "10 s – 1 min", value: 23 },
  { label: "1 – 5 min", value: 41 },
  { label: "5 min +", value: 22 },
];

const FEEDBACK = [
  { key: "understanding", label: "Understanding", value: 8.9 },
  { key: "relevance", label: "Relevance", value: 8.2 },
  { key: "actionability", label: "Actionability", value: 7.4 },
];

const DEVICES = [
  { label: "Mobile", value: 71, color: ACCENT },
  { label: "Tablet", value: 11, color: NEUTRAL },
  { label: "Desktop", value: 18, color: SOFT },
];

const LANGUAGES = [
  { key: "en", label: "EN", value: 54 },
  { key: "de", label: "DE", value: 29 },
  { key: "es", label: "ES", value: 10 },
  { key: "fr", label: "FR", value: 7 },
];

const VIEWS_AND_TAPS = Array.from({ length: 30 }, (_, index) => {
  const day = new Date(Date.UTC(2026, 8, 1 + index));
  const launch = index >= 6 && index <= 9 ? 60 - (index - 6) * 12 : 0;
  const views = Math.round(8 + launch + ((index * 7) % 9));
  return {
    iso_day: day.toISOString().slice(0, 10),
    views,
    interactions: Math.round(views * 2.4 + ((index * 5) % 7)),
  };
});

function moduleLabel(type: DraggableModuleType) {
  return getModuleTypeMeta(type).label;
}

const MODULE_TAPS: { type: DraggableModuleType; value: number }[] = [
  { type: "table", value: 68 },
  { type: "metric-tabs-chart", value: 42 },
  { type: "bar", value: 31 },
  { type: "ring-kpis", value: 12 },
];

const MODULE_ATTENTION: { type: DraggableModuleType; ms: number }[] = [
  { type: "table", ms: 24_000 },
  { type: "bar", ms: 14_000 },
  { type: "insight-panel", ms: 9_000 },
];

const ELEMENT_TAPS = [
  { key: "q3", label: "Q3", value: 42 },
  { key: "q4", label: "Q4", value: 35 },
  { key: "legend-costs", label: "Legend: costs", value: 18 },
  { key: "q1", label: "Q1", value: 9 },
];

export function ViewsPerPageMock() {
  return (
    <MockFrame icon={BarChart3} label="Views per page">
      <DistributionChart rows={VIEWS_PER_PAGE} valueLabel="Views" />
    </MockFrame>
  );
}

export function ReachPerPageMock() {
  return (
    <MockFrame icon={Eye} label="Reach per page">
      <DistributionChart
        rows={REACH_PER_PAGE}
        valueLabel="Reached"
        formatValue={percent}
      />
      <p className="mt-2 text-xs text-muted-foreground">
        <span className="font-medium text-primary">
          25 % stop after page 4
        </span>
        , the biggest drop.
      </p>
    </MockFrame>
  );
}

export function ReactionsMock() {
  return (
    <MockFrame icon={ThumbsUp} label="Reactions" aside="All pages">
      <ShareRing
        items={[
          { label: "Helpful", value: 91, color: ACCENT, sub: "128" },
          { label: "Not helpful", value: 9, color: NEUTRAL, sub: "12" },
        ]}
      />
    </MockFrame>
  );
}

export function FeedbackMock() {
  return (
    <MockFrame icon={Star} label="Feedback" aside="42 submissions">
      <div className="mb-4 flex items-baseline gap-1.5">
        <span className="text-[28px] font-medium leading-none tabular-nums text-neutral-900">
          8.6
        </span>
        <span className="text-[13px] text-muted-foreground">
          overall, of 10
        </span>
      </div>
      <BarList
        accentFirst={false}
        rows={FEEDBACK.map((row) => ({
          ...row,
          display: row.value.toFixed(1),
        }))}
      />
    </MockFrame>
  );
}

export function SessionLengthMock() {
  return (
    <MockFrame icon={Timer} label="Session length">
      <DistributionChart
        rows={SESSION_LENGTH}
        valueLabel="Sessions"
        formatValue={percent}
      />
    </MockFrame>
  );
}

export function DevicesMock() {
  return (
    <MockFrame icon={Smartphone} label="Devices">
      <ShareRing items={DEVICES} />
    </MockFrame>
  );
}

export function LanguagesMock() {
  return (
    <MockFrame icon={Globe} label="Languages" aside="Browser language">
      <BarList
        rows={LANGUAGES.map((row) => ({
          ...row,
          display: percent(row.value),
        }))}
      />
    </MockFrame>
  );
}

export function ModuleTapsMock() {
  return (
    <MockFrame icon={MousePointerClick} label="Taps by module">
      <BarList
        rows={MODULE_TAPS.map(({ type, value }) => ({
          key: type,
          label: moduleLabel(type),
          value,
        }))}
      />
    </MockFrame>
  );
}

export function PageStatsMock() {
  return (
    <MockFrame
      icon={BarChart3}
      label="Page 4"
      aside="Revenue by quarter"
      className="max-w-[320px]"
    >
      <div className="grid grid-cols-2 gap-2">
        <StatTile label="Visits" value="112" hint="Page views" />
        <StatTile label="Time on page" value="42 s" hint="Average per visit" />
        <StatTile
          label="Stopped reading here"
          value="25 %"
          accent
          hint="Of all viewers"
        />
        <StatTile label="Passthroughs" value="9" hint="Left within 5 seconds" />
      </div>
    </MockFrame>
  );
}

export function AttentionByModuleMock() {
  return (
    <MockFrame
      icon={Clock}
      label="Attention by module"
      aside="Per engaged viewer"
      className="max-w-[320px]"
    >
      <BarList
        rows={MODULE_ATTENTION.map(({ type, ms }) => ({
          key: type,
          label: moduleLabel(type),
          value: ms,
          display: `${Math.round(ms / 1000)} s`,
        }))}
      />
    </MockFrame>
  );
}

export function TapsByElementMock() {
  return (
    <MockFrame
      icon={MousePointerClick}
      label="Taps by element"
      aside={moduleLabel("bar")}
      className="max-w-[320px]"
    >
      <BarList rows={ELEMENT_TAPS} />
    </MockFrame>
  );
}

export function ViewerFlowMock() {
  return (
    <MockFrame
      icon={Workflow}
      label="Viewer flow"
      aside="Page 4"
      className="max-w-[320px]"
    >
      <PageFlowChart pageNumber={4} reached={112} continued={84} isLast={false} />
    </MockFrame>
  );
}

export function EngagementTabMock() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <StatTile label="Views" value="148" hint="Sessions started" />
        <StatTile label="Average time" value="4m 12s" hint="Per session" />
        <StatTile label="Read to the end" value="54 %" hint="Of all viewers" />
        <StatTile label="Helpful" value="91 %" hint="140 reactions" />
      </div>
      <div className="rounded-lg p-3 ring-1 ring-border">
        <div className="mb-2 text-[13px] font-medium">Views and taps</div>
        <ViewsAreaChart data={VIEWS_AND_TAPS} />
      </div>
    </div>
  );
}

export function FeedbackTabMock() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="rounded-lg p-4 ring-1 ring-border">
        <FeedbackMock />
      </div>
      <div className="rounded-lg p-4 ring-1 ring-border">
        <MockFrame icon={Star} label="Overall rating" aside="Ratings per score">
          <DistributionChart
            rows={[
              { label: "6", value: 3 },
              { label: "7", value: 5 },
              { label: "8", value: 11 },
              { label: "9", value: 14 },
              { label: "10", value: 9 },
            ]}
            valueLabel="Ratings"
          />
        </MockFrame>
      </div>
    </div>
  );
}

export function ReachTabMock() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="rounded-lg p-4 ring-1 ring-border">
        <DevicesMock />
      </div>
      <div className="rounded-lg p-4 ring-1 ring-border">
        <LanguagesMock />
      </div>
    </div>
  );
}
